/**
 * PinnedCallouts — Weidner Classic override
 *
 * Tower 12–style fixed-background parallax: the image is position:fixed
 * (pinned to the viewport) and clipped to the section via clip-path, so
 * page content scrolls over it. Left translucent copy panel; right half
 * stays open so the pinned image shows through.
 */

import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';

// ============================================================================
// Types & slot defs
// ============================================================================

interface AmenityItem {
  title?: string | null;
  group?: string | null;
  description?: string | null;
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Parallax Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Amenities accordion
// ============================================================================

function AmenitiesAccordion({ groups }: { groups: [string, AmenityItem[]][] }) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (groups.length === 0) return null;

  return (
    <div className="pinned-callouts__accordions">
      {groups.map(([groupName, items]) => {
        const isOpen = openKey === groupName;
        const slug = groupName.replace(/\s+/g, '-').toLowerCase();
        const panelId = `pinned-callouts-panel-${slug}`;
        const buttonId = `pinned-callouts-btn-${slug}`;
        return (
          <div key={groupName} className={`pinned-callouts__accordion${isOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              id={buttonId}
              className="pinned-callouts__accordion-trigger"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenKey(isOpen ? null : groupName)}
            >
              <span className="pinned-callouts__accordion-label">{groupName}</span>
              <span className="pinned-callouts__accordion-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  {!isOpen && <line x1="12" y1="5" x2="12" y2="19" />}
                </svg>
              </span>
            </button>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              className="pinned-callouts__accordion-panel"
              hidden={!isOpen}
            >
              <ul className="pinned-callouts__amenity-list">
                {items.map((item, i) => (
                  <li key={`${item.title ?? 'amenity'}-${i}`}>
                    {item.title || 'Amenity'}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function PinnedCallouts() {
  const bg = useSlotGroup('pinnedcallouts.bg_image', bgImageDef);
  const amenities = useCollectionBinding<AmenityItem>('pinnedcallouts.amenities', {
    collection: 'amenities',
    defaultLimit: 60,
  });

  const groups = useMemo(() => {
    const map = new Map<string, AmenityItem[]>();
    for (const item of amenities ?? []) {
      if (!item?.title) continue;
      const key = (item.group || 'Amenities').trim() || 'Amenities';
      const list = map.get(key);
      if (list) list.push(item);
      else map.set(key, [item]);
    }
    return Array.from(map.entries());
  }, [amenities]);

  const copyRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pinned-callouts">
      {/*
        Fixed to the viewport; clip-path on the section keeps it visible
        only while this section is on screen — content scrolls over it.
      */}
      <div className="pinned-callouts__bg">
        <EditableSlotGroup groupPath="pinnedcallouts.bg_image" definition={bgImageDef}>
          {bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt={bg.alt || ''}
              sizes="100vw"
              className="pinned-callouts__image"
            />
          ) : (
            <div className="pinned-callouts__image-fallback" aria-hidden="true" />
          )}
        </EditableSlotGroup>
        <div className="pinned-callouts__overlay" aria-hidden="true" />
      </div>

      {/* Foreground scrolls over the pinned image */}
      <div className="pinned-callouts__foreground">
        <div ref={copyRef} className="pinned-callouts__copy">
          <div className="pinned-callouts__copy-inner">
            <div className="pinned-callouts__heading-row">
              <span className="pinned-callouts__rule" aria-hidden="true" />
              <RichTextContent
                path="pinnedcallouts.title"
                label="Title"
                defaultValue="<p>A Higher Quality of Living</p>"
                as="h2"
                className="pinned-callouts__title"
              />
            </div>

            <RichTextContent
              path="pinnedcallouts.body"
              label="Body"
              defaultValue="<p>Live in a gorgeous apartment with exceptional amenities in a friendly and active atmosphere. Enjoy living within the epitome of the urban experience with dining, shopping, and entertainment steps from your home.</p><p>Our community offers luxurious studio, one and two-bedroom pet-friendly apartments that showcase stunning views. Your apartment will have superior amenities, like a gourmet kitchen, quartz countertops, A/C, full-size in-unit laundry, and plank hardwood-style flooring.</p><p>Expand your living space with outstanding community amenities, including a rooftop sky lounge, a state-of-the-art fitness center, a theater, a reservable clubhouse, and more.</p>"
              as="div"
              className="pinned-callouts__body"
            />

            <EditableCollectionBinding
              bindingPath="pinnedcallouts.amenities"
              label="Amenity Accordion Lists"
              className="pinned-callouts__amenities-wrap"
            >
              <AmenitiesAccordion groups={groups} />
            </EditableCollectionBinding>
          </div>
        </div>

        <div className="pinned-callouts__spacer" aria-hidden="true" />
      </div>

      <style>{`
        .pinned-callouts {
          position: relative;
          width: 100%;
          /*
            Clips position:fixed descendants to this section's box so the
            pinned image only shows while the section is in view — same
            effect as Tower 12's background-attachment: fixed.
          */
          clip-path: inset(0);
          background: #111;
        }

        /* Pinned to the viewport; does not scroll with the page */
        .pinned-callouts__bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          height: 100dvh;
          z-index: 0;
          pointer-events: none;
        }

        /* Fill the fixed frame; re-enable clicks for the editor image picker */
        .pinned-callouts__bg > * {
          position: absolute;
          inset: 0;
          display: block;
          width: 100%;
          height: 100%;
          pointer-events: auto;
        }

        .pinned-callouts__image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        .pinned-callouts__image-fallback {
          width: 100%;
          height: 100%;
          background: color-mix(in srgb, var(--vibe-color-primary) 45%, #222);
        }

        .pinned-callouts__overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.48);
          pointer-events: none;
        }

        /* Scrolls over the fixed image */
        .pinned-callouts__foreground {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1fr;
          min-height: 100vh;
        }
        @media (min-width: 992px) {
          .pinned-callouts__foreground {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            min-height: 0;
          }
        }

        .pinned-callouts__copy {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
          color: #222;
          background: rgba(255, 255, 255, 0.9);
          border-inline: 3px solid rgba(0, 0, 0, 0.06);
          padding: 3rem 1.5rem 3.5rem;
          order: 1;
        }
        @media (min-width: 768px) {
          .pinned-callouts__copy {
            padding: 3.5rem 2.75rem 4rem;
          }
        }
        @media (min-width: 992px) {
          .pinned-callouts__copy {
            order: 0;
            margin: 0 0 0 clamp(1.5rem, 3vw, 2.5rem);
            padding: 4.5rem 3rem 5rem;
            min-height: 100vh;
            background: rgba(255, 255, 255, 0.88);
          }
        }

        .pinned-callouts__copy-inner {
          width: 100%;
          max-width: 34rem;
          color: #222;
          overflow-x: hidden;
        }

        .pinned-callouts__heading-row {
          display: flex;
          align-items: stretch;
          gap: 1.15rem;
        }

        .pinned-callouts__rule {
          width: 4px;
          flex-shrink: 0;
          background: #54595f;
          align-self: stretch;
        }

        .pinned-callouts__title {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: clamp(2rem, 4.2vw, 3.35rem);
          font-weight: 700;
          letter-spacing: 0.02em;
          line-height: 1.05;
          text-transform: uppercase;
        }
        .pinned-callouts__title,
        .pinned-callouts__title p,
        .pinned-callouts__title span {
          color: #b6b6b6;
        }
        .pinned-callouts__title p { margin: 0; }

        /*
          Title slides in from the left when the copy panel enters view.
          useScrollReveal toggles reveal--hidden / reveal--visible on
          .pinned-callouts__copy (SSG-safe: starts at rest). Body and
          amenity lists keep the previous fade-up so only the heading
          changes direction.
        */
        .pinned-callouts__heading-row,
        .pinned-callouts__body,
        .pinned-callouts__amenities-wrap {
          transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .pinned-callouts__copy.reveal--hidden .pinned-callouts__heading-row {
          opacity: 0;
          transform: translateX(-40px);
        }
        .pinned-callouts__copy.reveal--hidden .pinned-callouts__body,
        .pinned-callouts__copy.reveal--hidden .pinned-callouts__amenities-wrap {
          opacity: 0;
          transform: translateY(24px);
        }
        .pinned-callouts__copy.reveal--visible .pinned-callouts__heading-row,
        .pinned-callouts__copy.reveal--visible .pinned-callouts__body,
        .pinned-callouts__copy.reveal--visible .pinned-callouts__amenities-wrap {
          opacity: 1;
          transform: none;
        }
        .pinned-callouts__copy.reveal--visible .pinned-callouts__body {
          transition-delay: 0.12s;
        }
        .pinned-callouts__copy.reveal--visible .pinned-callouts__amenities-wrap {
          transition-delay: 0.24s;
        }

        .pinned-callouts__body {
          margin: 1.5rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 400;
          line-height: 2;
        }
        .pinned-callouts__body,
        .pinned-callouts__body p,
        .pinned-callouts__body span {
          color: #444;
        }
        .pinned-callouts__body p {
          margin: 0 0 1.1rem;
        }
        .pinned-callouts__body p:last-child {
          margin-bottom: 0;
        }

        .pinned-callouts__amenities-wrap {
          display: block;
          margin-top: 2rem;
        }

        .pinned-callouts__accordions {
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }

        .pinned-callouts__accordion {
          border-top: 1px solid rgba(0, 0, 0, 0.12);
        }

        .pinned-callouts__accordion-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.85rem 0;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.05rem, 1.6vw, 1.35rem);
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          line-height: 1.4;
          transition: color 0.2s ease;
          color: #1a1a1a !important;
        }
        .pinned-callouts__accordion-trigger:hover {
          color: var(--vibe-color-primary) !important;
        }

        .pinned-callouts__accordion-label {
          color: inherit;
        }

        .pinned-callouts__accordion-icon {
          flex-shrink: 0;
          display: inline-flex;
          color: var(--vibe-color-primary);
        }

        .pinned-callouts__accordion-panel {
          padding: 0 0 1rem;
        }

        .pinned-callouts__amenity-list {
          margin: 0;
          padding: 0 0 0 0.15rem;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }
        .pinned-callouts__amenity-list li {
          position: relative;
          padding-left: 1.15rem;
          font-family: var(--vibe-font-body);
          font-size: 0.92rem;
          line-height: 1.55;
          color: #444;
        }
        .pinned-callouts__amenity-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.55em;
          width: 0.35rem;
          height: 0.35rem;
          border-radius: 1px;
          background: var(--vibe-color-primary);
        }

        /* Open half — pinned image visible underneath */
        .pinned-callouts__spacer {
          order: -1;
          min-height: 55vw;
          pointer-events: none;
        }
        @media (min-width: 768px) {
          .pinned-callouts__spacer {
            min-height: 420px;
          }
        }
        @media (min-width: 992px) {
          .pinned-callouts__spacer {
            order: 0;
            min-height: 100%;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .pinned-callouts__bg {
            position: absolute;
            inset: 0;
            height: 100%;
          }
          .pinned-callouts__heading-row,
          .pinned-callouts__body,
          .pinned-callouts__amenities-wrap,
          .pinned-callouts__copy.reveal--hidden .pinned-callouts__heading-row,
          .pinned-callouts__copy.reveal--hidden .pinned-callouts__body,
          .pinned-callouts__copy.reveal--hidden .pinned-callouts__amenities-wrap {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>A Higher Quality of Living</p>',
    },
    'pinnedcallouts.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Live in a gorgeous apartment with exceptional amenities in a friendly and active atmosphere. Enjoy living within the epitome of the urban experience with dining, shopping, and entertainment steps from your home.</p><p>Our community offers luxurious studio, one and two-bedroom pet-friendly apartments that showcase stunning views. Your apartment will have superior amenities, like a gourmet kitchen, quartz countertops, A/C, full-size in-unit laundry, and plank hardwood-style flooring.</p><p>Expand your living space with outstanding community amenities, including a rooftop sky lounge, a state-of-the-art fitness center, a theater, a reservable clubhouse, and more.</p>',
    },
  },
  slotGroups: {
    'pinnedcallouts.bg_image': bgImageDef,
  },
  collectionBindings: {
    'pinnedcallouts.amenities': {
      label: 'Amenity Accordion Lists',
      collection: 'amenities',
      defaultLimit: 60,
      description: 'Amenities shown in the expandable accordion panels, grouped by amenity group.',
    },
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
